import { Link } from "react-router-dom";
import { ArrowRight, FileText, Download, Play } from "lucide-react";
import { site } from "@/content/site";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { PhotoFrame } from "@/components/PhotoFrame";

export default function EPK() {
  return (
    <>
      <SEO
        title="EPK | EVE Band — Electronic Press Kit"
        description="EVE's official Electronic Press Kit — band profile, live performance footage, EPK PDF and technical rider downloads for promoters, venues, media and sponsors."
      />
      <section data-testid="epk-page" className="pb-24 pt-32 lg:pb-32 lg:pt-40">
        <div className="container-eve">
          <Reveal>
            <p className="eyebrow mb-6">For promoters, venues &amp; media</p>
            <h1 className="font-display text-5xl uppercase leading-[0.95] sm:text-6xl lg:text-7xl">
              Electronic
              <br />
              press kit<span className="text-eve-cyan">.</span>
            </h1>
            <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.35em] text-eve-silver">
              The ultimate rock party experience
            </p>
          </Reveal>

          <div className="mt-16 grid gap-12 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-20">
            <Reveal>
              <PhotoFrame
                src={site.photos.epk.path}
                alt={site.photos.epk.alt}
                eager
                testId="epk-band-photo"
                className="aspect-[4/5] w-full"
              />
              <p className="mt-6 text-base leading-relaxed text-eve-silver">
                {site.band.epkIntro}
              </p>
            </Reveal>

            <div>
              {/* Band profile */}
              <Reveal delay={0.05}>
                <div className="border border-white/10 bg-eve-surface/60 p-8" data-testid="epk-profile">
                  <h2 className="font-display text-2xl uppercase tracking-wide">EVE — profile</h2>
                  <dl className="mt-6 grid gap-4 sm:grid-cols-2">
                    {[
                      ["Band", "EVE"],
                      ["Origin", "Indonesia"],
                      ["Genre", site.band.genre],
                      ["Home base", site.band.location],
                    ].map(([k, v]) => (
                      <div key={k} className="border-l-2 border-eve-cyan/50 pl-4">
                        <dt className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/35">
                          {k}
                        </dt>
                        <dd className="mt-1.5 text-sm text-eve-ink">{v}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </Reveal>

              {/* Featured performance */}
              <Reveal delay={0.1}>
                <Link
                  to={site.epk.featuredPerformance.path}
                  data-testid="epk-featured-performance"
                  className="group mt-8 flex items-center justify-between gap-6 border border-white/10 bg-eve-surface/60 p-8 transition-colors duration-300 hover:border-eve-cyan/50"
                >
                  <div>
                    <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-white/35">
                      Featured performance
                    </p>
                    <p className="mt-3 font-display text-2xl uppercase">
                      {site.epk.featuredPerformance.title}
                    </p>
                  </div>
                  <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-white/20 text-white/70 transition-colors duration-300 group-hover:border-eve-cyan group-hover:text-eve-cyan">
                    <Play size={18} className="ml-0.5" />
                  </span>
                </Link>
              </Reveal>

              {/* Downloads */}
              <Reveal delay={0.15}>
                <h2 className="mt-14 font-display text-2xl uppercase tracking-wide">
                  Press materials<span className="text-eve-cyan">.</span>
                </h2>
                <ul className="mt-6 divide-y divide-white/10 border-y border-white/10">
                  {site.epk.files.map((f) => (
                    <li
                      key={f.id}
                      className="flex flex-wrap items-center justify-between gap-4 py-5"
                      data-testid={`epk-download-${f.id}`}
                    >
                      <span className="flex items-center gap-4">
                        <FileText size={18} className="text-eve-silver" />
                        <span className="text-sm text-eve-ink">{f.label}</span>
                      </span>
                      {f.available ? (
                        <a
                          href={f.path}
                          download
                          data-testid={`epk-download-btn-${f.id}`}
                          className="btn-outline px-5 py-2.5 text-[10px]"
                        >
                          Download <Download size={12} />
                        </a>
                      ) : (
                        <span
                          className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/30"
                          title="Drop the file into /public/assets/docs and set available: true in content settings"
                        >
                          Pending · {f.path}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="mt-12 flex flex-col items-start gap-5 border border-eve-cyan/25 bg-eve-cyan/[0.04] p-8 sm:flex-row sm:items-center sm:justify-between">
                  <p className="font-display text-2xl uppercase">
                    Ready to bring EVE
                    <br className="hidden sm:block" /> to your event?
                  </p>
                  <Link to="/contact" data-testid="epk-contact-management-btn" className="btn-primary shrink-0">
                    Contact management <ArrowRight size={14} />
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
